import { StandardEvents } from '@shopify/events';

class ShippingCalculator extends HTMLElement {
  static queue = Promise.resolve();

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-shipping-postal-code]');
    this.button = this.querySelector('[data-shipping-submit]');
    this.buttonLabel = this.querySelector('[data-shipping-button-label]');
    this.location = this.querySelector('[data-shipping-location]');
    this.message = this.querySelector('[data-shipping-message]');
    this.results = this.querySelector('[data-shipping-results]');
    this.resultsList = this.querySelector('[data-shipping-results-list]');

    if (!this.form || !this.input || !this.button || !this.buttonLabel || !this.message || !this.resultsList) return;

    this.form.addEventListener('submit', this.handleSubmit, { signal: this.abortController.signal });
    this.input.addEventListener('input', this.handleInput, { signal: this.abortController.signal });

    const productContainer = this.closest('.shopify-section, dialog');
    productContainer?.addEventListener(StandardEvents.productSelect, this.handleProductSelect, {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  handleInput = () => {
    const digits = this.input.value.replace(/\D/g, '').slice(0, 8);
    this.input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    this.input.setAttribute('aria-invalid', 'false');
  };

  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        if (detail.newProduct) {
          this.dataset.productId = String(detail.newProduct.id);
        } else if (detail.productId && String(detail.productId) !== this.dataset.productId) {
          return;
        }

        this.dataset.variantId = detail.resource?.id ? String(detail.resource.id) : '';
        this.dataset.variantAvailable = detail.resource && detail.resource.available !== false ? 'true' : 'false';
        if (typeof detail.resource?.requires_shipping === 'boolean') {
          this.dataset.requiresShipping = String(detail.resource.requires_shipping);
        }
        this.clearOutput();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[shipping-calculator] Variant update failed:', error);
      });
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    if (this.button.disabled) return;

    const postalCode = this.input.value.replace(/\D/g, '');
    if (postalCode.length !== 8) {
      this.showError('Digite um CEP válido com 8 números.');
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus();
      return;
    }

    const variantId = this.getSelectedVariantId();
    if (!variantId || this.dataset.variantAvailable === 'false') {
      this.showError('Selecione uma opção disponível do produto.');
      return;
    }
    if (this.dataset.requiresShipping === 'false') {
      this.showError('Este produto está configurado na Shopify como não exigindo frete.');
      return;
    }

    this.setBusy(true);
    this.clearOutput();

    try {
      const address = await this.lookupPostalCode(postalCode);
      if (address?.invalid) {
        this.showError('CEP não encontrado.');
        return;
      }

      if (address && this.dataset.showLocation === 'true') {
        this.showLocation(address);
      }

      const rates = await this.enqueue(() => this.calculateRates(postalCode, address?.uf, variantId));
      this.showRates(rates);
    } catch (error) {
      console.error('[shipping-calculator]', error);
      this.showError(error?.userMessage || 'Não foi possível calcular o frete. Tente novamente.');
    } finally {
      this.setBusy(false);
    }
  };

  getSelectedVariantId() {
    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    const productInformation = this.closest('[id*="ProductInformation-"]');
    const formVariant = productInformation?.querySelector('product-form-component input[name="id"]');
    return urlVariant || formVariant?.value || this.dataset.variantId;
  }

  getSelectedSellingPlan() {
    const productInformation = this.closest('[id*="ProductInformation-"]');
    return productInformation?.querySelector('product-form-component [name="selling_plan"]')?.value || '';
  }

  async lookupPostalCode(postalCode) {
    if (this.dataset.showLocation !== 'true') return null;

    try {
      const response = await fetch(`https://viacep.com.br/ws/${postalCode}/json/`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;

      const address = await response.json();
      return address.erro ? { invalid: true } : address;
    } catch (_) {
      // Address display is optional; Shopify can still calculate using the postal code.
      return null;
    }
  }

  enqueue(task) {
    const run = ShippingCalculator.queue.then(task, task);
    ShippingCalculator.queue = run.catch(() => {});
    return run;
  }

  async calculateRates(postalCode, province, variantId) {
    const root = window.Shopify?.routes?.root || '/';
    const token = `shipping-estimate-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const item = {
      id: variantId,
      quantity: 1,
      properties: {
        _shipping_estimate: token,
      },
    };
    const sellingPlan = this.getSelectedSellingPlan();
    if (sellingPlan) item.selling_plan = sellingPlan;

    let temporaryLineKey = '';
    let rates;
    let calculationError;
    let cleanupError;

    try {
      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: [item] }),
      });
      const added = await this.parseResponse(addResponse);
      temporaryLineKey = added.items?.[0]?.key || '';

      if (!temporaryLineKey) {
        throw this.createError('Não foi possível preparar o produto para o cálculo.');
      }

      const params = new URLSearchParams({
        'shipping_address[zip]': postalCode,
        'shipping_address[country]': 'Brazil',
      });
      if (province) params.set('shipping_address[province]', province);

      rates = await this.fetchShippingRates(root, params);
    } catch (error) {
      calculationError = error;
    } finally {
      if (temporaryLineKey) {
        try {
          await this.removeTemporaryLine(root, temporaryLineKey);
        } catch (_) {
          try {
            await this.removeTemporaryLine(root, temporaryLineKey);
          } catch (error) {
            cleanupError = error;
          }
        }
      }
    }

    if (cleanupError) {
      throw this.createError(
        'O cálculo foi interrompido e não foi possível restaurar o carrinho. Atualize a página e confira o carrinho.'
      );
    }
    if (calculationError) throw calculationError;

    return rates;
  }

  async fetchShippingRates(root, params) {
    params.set('_shipping_estimate_timestamp', String(Date.now()));
    const ratesResponse = await fetch(`${root}cart/shipping_rates.json?${params.toString()}`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    const ratesPayload = await this.parseResponse(ratesResponse);
    return Array.isArray(ratesPayload.shipping_rates) ? ratesPayload.shipping_rates : [];
  }

  async removeTemporaryLine(root, lineKey) {
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });
    await this.parseResponse(response);
  }

  async parseResponse(response) {
    let payload;
    try {
      payload = await response.json();
    } catch (_) {
      payload = {};
    }

    if (!response.ok || payload?.status) {
      const responseError = Array.isArray(payload?.error) ? payload.error.join(' ') : payload?.error;
      const message =
        payload?.description || payload?.message || responseError || 'A Shopify não retornou uma cotação.';
      const userMessage = message.includes('não exige frete')
        ? 'Este produto está configurado na Shopify como não exigindo frete.'
        : message;
      throw this.createError(userMessage);
    }
    return payload ?? {};
  }

  createError(message) {
    const error = new Error(message);
    error.userMessage = message;
    return error;
  }

  showLocation(address) {
    if (!this.location) return;
    const street = address.logradouro || address.bairro;
    const place = [address.localidade, address.uf].filter(Boolean).join('/');
    this.location.textContent = [street, place].filter(Boolean).join(' — ');
    this.location.hidden = !this.location.textContent;
  }

  showRates(rates) {
    this.resultsList.replaceChildren();

    if (!rates.length) {
      this.showError('Nenhuma opção de frete foi encontrada para este CEP.');
      return;
    }

    const formatter = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: this.dataset.currency || 'BRL',
    });

    for (const rate of rates) {
      const listItem = document.createElement('li');
      listItem.className = 'shipping-calculator__rate';

      const rateInformation = document.createElement('span');
      rateInformation.className = 'shipping-calculator__rate-information';

      const name = document.createElement('span');
      name.className = 'shipping-calculator__rate-name';
      name.textContent = rate.name;
      rateInformation.append(name);

      const deliveryText = this.formatDeliveryTime(rate);
      if (deliveryText) {
        const delivery = document.createElement('span');
        delivery.className = 'shipping-calculator__rate-delivery';
        delivery.textContent = deliveryText;
        rateInformation.append(delivery);
      }

      const price = document.createElement('span');
      price.className = 'shipping-calculator__rate-price';
      const numericPrice = Number.parseFloat(rate.price);
      price.textContent =
        rate.price_with_currency || (Number.isFinite(numericPrice) ? formatter.format(numericPrice) : rate.price);

      listItem.append(rateInformation, price);
      this.resultsList.append(listItem);
    }

    this.results.hidden = false;
    this.message.hidden = true;
  }

  formatDeliveryTime(rate) {
    const businessDays = this.businessDaysUntil(rate.delivery_date);
    if (Number.isFinite(businessDays)) {
      return `Entrega estimada: ${businessDays} ${businessDays === 1 ? 'dia útil' : 'dias úteis'}`;
    }

    const deliveryDays = Array.isArray(rate.delivery_days)
      ? rate.delivery_days.map(Number).filter(Number.isFinite)
      : [Number(rate.delivery_days)].filter(Number.isFinite);

    if (!deliveryDays.length) return '';

    const minimumDays = Math.min(...deliveryDays);
    const maximumDays = Math.max(...deliveryDays);
    if (minimumDays === maximumDays) {
      return `Entrega estimada: ${maximumDays} ${maximumDays === 1 ? 'dia útil' : 'dias úteis'}`;
    }

    return `Entrega estimada: de ${minimumDays} a ${maximumDays} dias úteis`;
  }

  businessDaysUntil(date) {
    if (!date) return Number.NaN;

    const deliveryDate = new Date(`${date}T12:00:00`);
    if (Number.isNaN(deliveryDate.getTime())) return Number.NaN;

    const cursor = new Date();
    cursor.setHours(12, 0, 0, 0);
    deliveryDate.setHours(12, 0, 0, 0);

    if (deliveryDate <= cursor) return 0;

    let businessDays = 0;
    while (cursor < deliveryDate) {
      cursor.setDate(cursor.getDate() + 1);
      const dayOfWeek = cursor.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) businessDays += 1;
    }

    return businessDays;
  }

  showError(message) {
    this.results.hidden = true;
    this.message.textContent = message;
    this.message.hidden = false;
  }

  clearOutput() {
    this.resultsList?.replaceChildren();
    if (this.results) this.results.hidden = true;
    if (this.location) {
      this.location.textContent = '';
      this.location.hidden = true;
    }
    if (this.message) {
      this.message.textContent = '';
      this.message.hidden = true;
    }
  }

  setBusy(isBusy) {
    this.button.disabled = isBusy;
    this.input.disabled = isBusy;
    this.setAttribute('aria-busy', String(isBusy));
    this.buttonLabel.textContent = isBusy ? this.dataset.loadingText : this.dataset.buttonText;
  }
}

if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCalculator);
}
